import { getPosts } from "../lib/data";
import PostCard from "../components/post-card";
import CreatePostForm from "../components/create-post-form";
import ReportDialog from "../components/report-dialog";
import { useAuth } from "../hooks/use-auth";
import { useEffect, useState, useRef, useMemo } from "react";
import { useOutbox } from "../hooks/use-outbox";
import type { Post, User } from "../types";
import { Loader2, Flag } from "lucide-react";
import { Button } from "../components/ui/button";
import { SlowLoadingNotice } from "../components/slow-loading-notice";
import { useSlowLoading } from "../hooks/use-slow-loading";

// Posts are loaded page by page (newest first); scrolling near the end loads
// the next page. Loading every post at once made the feed slow to open.
const PAGE_SIZE = 10;

export default function FeedPage() {
  const { user } = useAuth();
  const [posts, setPosts] = useState<(Post & { author: User })[]>([]);
  const [loading, setLoading] = useState(true);
  const [isReportDialogOpen, setIsReportDialogOpen] = useState(false);

  const [queryLimit, setQueryLimit] = useState(PAGE_SIZE);
  const [hasMore, setHasMore] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const slow = useSlowLoading(loading, "feed", attempt);
  const loadMoreRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (loading || !hasMore || !loadMoreRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setQueryLimit((prev) => (prev <= posts.length ? prev + PAGE_SIZE : prev));
        }
      },
      { threshold: 0.1, rootMargin: "600px" }
    );
    observer.observe(loadMoreRef.current);
    return () => observer.disconnect();
  }, [loading, hasMore, posts.length]);

  useEffect(() => {
    let retryTimer: ReturnType<typeof setTimeout> | undefined;
    const unsubscribe = getPosts(
      (newPosts, more) => {
        setPosts(newPosts);
        setHasMore(more);
        setLoading(false);
      },
      {
        limitCount: queryLimit,
        // A failed listener stops for good: keep what is shown and retry.
        onError: () => {
          retryTimer = setTimeout(() => setAttempt((n) => n + 1), 3000);
        },
      }
    );

    return () => {
      if (retryTimer) clearTimeout(retryTimer);
      unsubscribe();
    };
  }, [queryLimit, attempt]);

  // This person's posts that are still on their way (e.g. posted without
  // internet, then the app was closed): shown, marked "Šalje se…", until
  // they arrive.
  const outbox = useOutbox();
  const shownPosts = useMemo(() => {
    if (!user) return posts;
    const ids = new Set(posts.map((post) => post.id));
    const waiting = outbox
      .filter((entry) => entry.uid === user.id && /^posts\/[^/]+$/.test(entry.path))
      .map((entry) => ({ id: entry.path.slice("posts/".length), ...entry.data, author: user }) as Post & { author: User })
      .filter((post) => !ids.has(post.id));
    if (!waiting.length) return posts;
    return [...waiting, ...posts].sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  }, [posts, outbox, user]);

  const handlePostCreated = () => {
    // Optimistic update is no longer needed here as real-time listener will handle it.
    // However, keeping it can make the UI feel faster.
    // For simplicity with the new real-time approach, we can remove this line
    // as Firestore will push the update. If you notice a lag, we can add it back.
    // setPosts(prevPosts => [newPost, ...prevPosts]);
  };

  const handlePostDeleted = (deletedPostId: string) => {
    // With real-time updates, this manual state update is also not strictly necessary
    // but can prevent a flicker on the UI.
    setPosts((prevPosts) =>
      prevPosts.filter((post) => post.id !== deletedPostId)
    );
  };

  return (
    <>
      <div className="container mx-auto max-w-3xl">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold tracking-tight text-foreground font-headline">
            Glavni feed
          </h1>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setIsReportDialogOpen(true)}
            className="hover:bg-[#e73e7b]"
          >
            <Flag className="mr-2 h-4 w-4" /> Prijavi problem
          </Button>
        </div>

        {user && ["trainer", "admin", "glavni_trener"].includes(user.role) && (
          <CreatePostForm onPostCreated={handlePostCreated} />
        )}

        {loading ? (
          <div className="flex flex-col justify-center items-center gap-6 min-h-64 py-16">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            {slow && <SlowLoadingNotice screen="feed" />}
          </div>
        ) : (
          <div className="content-enter space-y-6">
            {shownPosts.map((post) => (
              <PostCard
                key={post.id}
                post={post}
                onPostDeleted={handlePostDeleted}
              />
            ))}
            {hasMore && (
              <div ref={loadMoreRef} className="py-6 flex justify-center items-center text-muted-foreground">
                <Loader2 className="h-6 w-6 animate-spin mr-2" />
                Učitavanje starijih objava...
              </div>
            )}
          </div>
        )}
      </div>
      <ReportDialog
        isOpen={isReportDialogOpen}
        onOpenChange={setIsReportDialogOpen}
      />
    </>
  );
}
